import { create } from 'zustand';

import { showErrorToast } from '@repo/ui/components/ui/error-toast';
import { createInitialState, InitialState } from '@repo/ui/state';
import { getPhotoVogueService } from '@/services/photovogue';
import type { DailyAssignment } from '@/services/photovogue';

const sortByDate = (assignments: DailyAssignment[]): DailyAssignment[] =>
  [...assignments].sort((a, b) => a.date.localeCompare(b.date));

export interface DailiesStore extends InitialState<DailyAssignment[]> {
  fetchCalendar: (dateFrom: string, dateTo: string) => Promise<void>;
  assign: (date: string, photoId: string) => Promise<DailyAssignment | null>;
  remove: (date: string) => Promise<void>;
  reset: () => void;
}

export const initialState = {
  ...createInitialState<DailyAssignment[]>([]),
};

export const store = create<DailiesStore>((set) => ({
  ...initialState,

  fetchCalendar: async (dateFrom, dateTo) => {
    set((state) => ({ ...state, loading: true, error: null }));
    try {
      const data = await getPhotoVogueService().getDailies(dateFrom, dateTo);
      set((state) => ({ ...state, data, loading: false }));
    } catch (error) {
      showErrorToast({ title: 'Unable to load the Pic of the Day calendar' });
      set((state) => ({
        ...state,
        error: (error as Error).message,
        loading: false,
      }));
    }
  },

  assign: async (date, photoId) => {
    try {
      const assignment = await getPhotoVogueService().setDaily(date, photoId);
      set((state) => ({
        ...state,
        data: sortByDate([
          ...state.data.filter(
            (item) =>
              item.date !== date && item.photo.id !== assignment.photo.id,
          ),
          assignment,
        ]),
      }));
      return assignment;
    } catch (error) {
      showErrorToast({ title: 'Unable to set the Pic of the Day' });
      set((state) => ({ ...state, error: (error as Error).message }));
      return null;
    }
  },

  remove: async (date) => {
    try {
      const assignment = store().data.find((item) => item.date === date);
      await getPhotoVogueService().removeDaily(date, assignment?.photo.id);
      set((state) => ({
        ...state,
        data: state.data.filter((item) => item.date !== date),
      }));
    } catch (error) {
      showErrorToast({ title: 'Unable to remove the Pic of the Day' });
      set((state) => ({ ...state, error: (error as Error).message }));
    }
  },

  reset: () => set((state) => ({ ...state, ...initialState })),
}));
